import { ArrowForwardOutlined, HistoryOutlined } from "@mui/icons-material";
import { Box, Button, Grid, Skeleton, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

// Define the NewConversationRequest type locally
interface NewConversationRequest {
  id: number;
  name?: string | null;
  create_date: string;
  /** Parts AI dialogue kind ("run" | "comparison" | "search"); absent for other products. */
  kind?: string | null;
}
import RecentChatCard from "./RecentChatCard";
import { styled } from "@mui/material/styles";
import { useGetAllDialogues } from "../../AssistantServices";
import { useHistory } from "react-router-dom";
import { useChatStore } from "src/views/assistant/stores/ChatStore";

const ErrorBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  color: theme.palette.error.main,
  textAlign: "center",
}));

const RecentChats = ({
  productKey,
  kind,
  recentsBasePath,
  renderChatBadge,
}: {
  productKey: string | undefined;
  /**
   * Optional dialogue-kind filter. When set, the list is restricted to that kind
   * so the registered builder (which is kind-specific per splash) routes correctly.
   */
  kind?: string;
  /**
   * Overrides productConfig.basePath for the "View more" link only. Needed
   * when a product's own basePath (its new-chat route) isn't also where its
   * recents page lives — e.g. Parts Sourcing's basePath is "/parts/search",
   * but its recents share the Parts AI product's "/parts/recents".
   */
  recentsBasePath?: string;
  /**
   * Optional per-row badge (e.g. Parts AI's kind chip). Kept out of this
   * generic component — the product-specific caller supplies its own
   * renderer instead of RecentChats importing product UI directly.
   */
  renderChatBadge?: (dialogue: NewConversationRequest) => ReactNode;
}) => {
  const history = useHistory();
  const { basePath } = useChatStore((state) => state.productConfig);
  // Look up the builder for THIS list's product, not whatever was registered
  // globally last — so a stale builder from another product can't misroute.
  const buildChatUrl = useChatStore((state) =>
    productKey ? state.buildChatUrlRegistry[productKey] : undefined,
  );
  const sanitizedBasePath = basePath.endsWith("/")
    ? basePath.slice(0, -1)
    : basePath;
  const rawRecentsBasePath = recentsBasePath ?? sanitizedBasePath;
  const sanitizedRecentsBasePath = rawRecentsBasePath.endsWith("/")
    ? rawRecentsBasePath.slice(0, -1)
    : rawRecentsBasePath;
  const recentsHref = kind
    ? `${sanitizedRecentsBasePath}/recents?kind=${encodeURIComponent(kind)}`
    : `${sanitizedRecentsBasePath}/recents`;

  // `kind` is forwarded as an extra /chat/dialogue query filter.
  const { data: dialogues, status } = useGetAllDialogues(
    "",
    productKey,
    kind ? { kind } : undefined,
  );

  const recentChats = dialogues?.pages?.[0]?.data?.slice(0, 3) || [];

  const viewMoreTestId =
    productKey === "proposal-generation" ? "propgen-btn-view-more" : undefined;

  const navigateToChat = async (dialogue: NewConversationRequest) => {
    // Use custom URL builder if provided, otherwise use default pattern
    const url = buildChatUrl
      ? await buildChatUrl(dialogue.id, dialogue.kind)
      : `${sanitizedBasePath}/chat/${dialogue.id}`;
    history.push(url);
  };

  return (
    <Box sx={{ py: 4, maxWidth: "830px", width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HistoryOutlined color="inherit" />
          <Typography variant="body1">Recent Chats</Typography>
        </Box>
        <Button
          endIcon={<ArrowForwardOutlined />}
          variant="text"
          color="inherit"
          onClick={() => history.push(recentsHref)}
          data-attr="assistant:link:view-all-recent-chats"
          data-testid={viewMoreTestId}
        >
          View more
        </Button>
      </Box>

      {status === "loading" && (
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            gap={2}
            sx={{ width: "100%", maxWidth: "830px" }}
          >
            <Skeleton variant="rectangular" width={270} height={110} />
            <Skeleton variant="rectangular" width={270} height={110} />
            <Skeleton variant="rectangular" width={270} height={110} />
          </Stack>
        </Box>
      )}

      {status === "error" && (
        <ErrorBox>We're having trouble fetching the chat history.</ErrorBox>
      )}

      {status === "success" && (
        <Grid container spacing={2} data-testid="aa-history-list">
          {recentChats.length > 0 ? (
            recentChats.map((dialogue: NewConversationRequest) => (
              <Grid item xs={12} md={4} key={dialogue.id}>
                <RecentChatCard
                  dialogue={dialogue}
                  onClick={navigateToChat}
                  renderBadge={renderChatBadge}
                />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center">
                No chats yet...
              </Typography>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default RecentChats;
